import { useUpdateCanvas } from "@/api/canvas";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import type { Canvas } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/**
 * Move to for canvases. A canvas is filed in the workspace tree beside files,
 * so the picker drills the same folder tree a file's Move to does and the
 * commit is one request: the new folder, and the new workspace when the move
 * crosses one.
 *
 * Copy is deliberately absent. Duplicating a canvas would have to decide what
 * happens to the references it holds, and that is unspecified; Move needs no
 * such ruling because the canvas keeps its identity and its contents.
 */
export function useCanvasMoveCommit(
  canvas: Canvas | null,
): (destination: TransferDestination) => void {
  const updateCanvas = useUpdateCanvas({});

  return useCallback(
    (destination: TransferDestination) => {
      if (!canvas?.id) return;
      void (async () => {
        try {
          const crossWorkspace =
            destination.workspaceId !== canvas.workspace_id;
          await updateCanvas.mutateAsync({
            id: canvas.id,
            data: {
              parent_path: destination.folderPath,
              ...(crossWorkspace
                ? { workspace_id: destination.workspaceId }
                : {}),
            },
          });
          toast.success(`Moved to ${destination.label}`);
        } catch {
          toast.error("Failed to move canvas");
        }
      })();
    },
    [canvas, updateCanvas],
  );
}

/**
 * Decorate a canvas's resolved actions with the workspace destination
 * submenu. Returns the actions unchanged when `move` isn't on the menu.
 */
export function useAttachCanvasTransferSubmenu(
  actions: ResolvedObjectAction[],
  canvas: Canvas | null,
): ResolvedObjectAction[] {
  const commitMove = useCanvasMoveCommit(canvas);
  const hasMove = actions.some((a) => a.id === "move");
  const writableWorkspaceIds = useWritableWorkspaceIds(hasMove);

  return useMemo(() => {
    if (!canvas?.id || !hasMove) return actions;
    const submenu: ObjectActionDestinationSubmenu = {
      kind: "workspace-destination",
      operation: "move",
      commit: commitMove,
      currentWorkspaceId: canvas.workspace_id ?? undefined,
      // The canvas object alone doesn't know its folder, and an unset
      // currentFolderPath would read as "at the root" — wrongly disabling
      // the root as a destination for a foldered canvas (3.5). A sentinel
      // that matches no real path disables nothing; a same-place move is a
      // harmless placement no-op.
      currentFolderPath: "\u0000unknown",
      writableWorkspaceIds,
    };
    return actions.map((action) =>
      action.id === "move" ? { ...action, submenu } : action,
    );
  }, [actions, canvas, commitMove, hasMove, writableWorkspaceIds]);
}
